<script setup lang="ts">
import VeeCheckbox from '@/components/forms/Checkbox/VeeCheckbox.vue';
import VeeNumberField from '@/components/forms/NumberField/VeeNumberField.vue';
import VeeSelect from '@/components/forms/Select/VeeSelect.vue';
import { SelectItemTypeObject } from '@/components/forms/types';
import { useFormMode } from '@/components/forms/useFormMode.ts';
import { NumberDisplayOptions } from '@/utils/numberUtils';

defineProps<{
  name: string; // taskItemAreas[{areaIndex}].taskTypeData.corrections
}>();

const mode = useFormMode();

// Without a floating-point format the number field rejects the decimal separator on input.
// The fraction digits only round the display in details mode.
const airTemperatureFormat: NumberDisplayOptions = {
  valueType: 'FloatingPoint',
  fractionDigits: 1,
};
const pressureFormat: NumberDisplayOptions = { valueType: 'FloatingPoint', fractionDigits: 2 };
const correctionFactorFormat: NumberDisplayOptions = {
  valueType: 'FloatingPoint',
  fractionDigits: 4,
};

const referenceTemperatureItems: SelectItemTypeObject<number>[] = [
  { title: '20 °C', value: 20 },
  { title: '22 °C', value: 22 },
];
</script>

<template>
  <v-card>
    <v-card-item>
      <v-card-title>
        <h5>{{ $t('Corrections') }}</h5>
      </v-card-title>
    </v-card-item>

    <v-card-text>
      <v-table class="corrections-table">
        <thead>
          <tr>
            <th>{{ $t('Name') }}</th>
            <th>{{ $t('DefaultValues') }}</th>
            <th class="text-center">{{ $t('AdjustValuesInMeasurement') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="font-weight-bold">{{ $t('AirTemperature') }}</td>
            <td>
              <VeeNumberField
                :name="`${name}.airTemperature`"
                :mode="mode"
                :format="airTemperatureFormat"
                suffix="°C"
              />
            </td>
            <td>
              <div class="d-flex justify-center">
                <VeeCheckbox :name="`${name}.adjustAirTemperature`" :mode="mode" />
              </div>
            </td>
          </tr>

          <tr>
            <td class="font-weight-bold">{{ $t('AirPressure') }}</td>
            <td>
              <VeeNumberField
                :name="`${name}.airPressure`"
                :mode="mode"
                :format="pressureFormat"
                suffix="hPa"
              />
            </td>
            <td>
              <div class="d-flex justify-center">
                <VeeCheckbox :name="`${name}.adjustAirPressure`" :mode="mode" />
              </div>
            </td>
          </tr>

          <tr>
            <td class="font-weight-bold">{{ $t('ReferenceTemperature') }}</td>
            <td>
              <VeeSelect
                :name="`${name}.referenceTemperature`"
                :mode="mode"
                :items="referenceTemperatureItems"
              />
            </td>
            <td>
              <div class="d-flex justify-center">
                <VeeCheckbox :name="`${name}.adjustReferenceTemperature`" :mode="mode" />
              </div>
            </td>
          </tr>

          <tr>
            <td class="font-weight-bold">{{ $t('ReferencePressure') }}</td>
            <td>
              <VeeNumberField
                :name="`${name}.referencePressure`"
                :mode="mode"
                :format="pressureFormat"
                suffix="hPa"
                disabled
              />
            </td>
            <td></td>
          </tr>

          <tr>
            <td class="font-weight-bold">{{ $t('CorrectionFactorKUser') }}</td>
            <td>
              <VeeNumberField
                :name="`${name}.correctionFactor`"
                :mode="mode"
                :format="correctionFactorFormat"
              />
            </td>
            <td>
              <div class="d-flex justify-center">
                <VeeCheckbox :name="`${name}.adjustCorrectionFactor`" :mode="mode" />
              </div>
            </td>
          </tr>
        </tbody>
      </v-table>
    </v-card-text>
  </v-card>
</template>

<style lang="scss" scoped>
.corrections-table {
  th:nth-child(1),
  td:nth-child(1) {
    width: 33%;
  }

  th:nth-child(2),
  td:nth-child(2) {
    width: 40%;
  }

  td {
    padding-top: 8px;
    padding-bottom: 8px;
    vertical-align: middle;
  }
}
</style>
